// Copyright (c) 2017, Shellyl_N and Authors
// license: ISC
// https://github.com/shellyln
import { Escape } from 'red-agate-util/modules/convert/Escape';
const HTML_NO_CLOSE_TAGS = new Set([
    'area', 'base', 'br', 'col', 'command', 'embed', 'hr',
    'img', 'input', 'keygen', 'link', 'meta', 'param', 'source', 'track', 'wbr',
]);
export class RedAgateComponent {
    // tslint:disable-next-line:no-object-literal-type-assertion
    constructor(props = {}) {
        this.props = props;
        this.savedContexts = new Map();
    }
    defer() {
        return Promise.resolve();
    }
    setContext(contexts, key, value) {
        if (this.savedContexts.has(key)) {
            throw new Error(`RedAgateComponent#setContext: the key '${key}' has already set by myself.`);
        }
        if (contexts.has(key)) {
            contexts.set(key, contexts.get(key));
        }
        contexts.set(key, value);
    }
    unsetContext(contexts, key) {
        contexts.delete(key);
        if (this.savedContexts.has(key)) {
            contexts.set(key, this.savedContexts.get(key));
            this.savedContexts.delete(key);
        }
    }
    getContext(contexts, key) {
        return contexts.get(key);
    }
}
export class RedAgatePhantomComponent extends RedAgateComponent {
    transform() {
        return ((this.props.children !== null && this.props.children !== void 0) ?
            this.props.children : []);
    }
}
class RedAgateSimpleComponent extends RedAgateComponent {
    constructor(fn, props) {
        super(props);
        this.fn = fn;
    }
    transform() {
        return this.fn(this.props);
    }
}
function getPromises(promises, element) {
    if (element === null || element === void 0) {
        return promises;
    }
    if (Array.isArray(element)) {
        element.forEach(x => getPromises(promises, x));
        return promises;
    }
    if (typeof element !== 'object') {
        return promises;
    }
    if (element.component && element.component.defer) {
        const el = element;
        const p = el.component.defer();
        if (p)
            promises.push(p);
    }
    if (element.children) {
        getPromises(promises, element.children);
    }
    return promises;
}
function buildElementProps(props, children) {
    if (children) {
        if (children.length === 1) {
            props.children = children[0];
        }
        else if (children.length > 1) {
            props.children = children;
        }
        let refs = {};
        let hasRefs = false;
        for (const c of children) {
            if (c === null || c === void 0) {
                // do nothing
            }
            else if (Array.isArray(c)) {
                // do nothing
            }
            else if (typeof c !== 'object') {
                // do nothing
            }
            else {
                if (c.props) {
                    const p = c.props;
                    if (typeof p.refs === 'object') {
                        hasRefs = true;
                        refs = Object.assign(refs, p.refs);
                    }
                    if (typeof p.ref === 'string') {
                        hasRefs = true;
                        refs[p.ref] = c;
                    }
                }
            }
        }
        if (hasRefs) {
            props.refs = refs;
        }
    }
    return props;
}
export function createElement(type, props, ...children) {
    // tslint:disable-next-line:no-object-literal-type-assertion
    props = buildElementProps(props || {}, children);
    if (typeof type === 'function') {
        return {
            type,
            props,
            nodeId: null,
            children,
            // ES6 arrow function have no prototype.
            component: type.prototype && type.prototype.earlyConstruct && type.prototype.transform ?
                new type(props) :
                null,
        };
    }
    if (typeof type === 'string') {
        return {
            type,
            props,
            nodeId: null,
            children,
            component: null,
        };
    }
    throw new Error('createElement receives invalid parameter type.');
}
export function cloneElement(el) {
    let props = Object.assign({}, el.props || {});
    delete props.__nodeId;
    const children = (Array.isArray(el.children) ? el.children.slice(0) : el.children);
    props = buildElementProps(props, children);
    return {
        type: el.type,
        props,
        nodeId: null,
        children,
        component: null,
    };
}
export function createElementFromComponentInstance(c) {
    return {
        type: c.constructor,
        props: c.props,
        nodeId: null,
        children: (Array.isArray(c.props.children) ? c.props.children : []),
        component: c,
    };
}
function constructComponent(el, transformContext) {
    if (el.nodeId === null || el.nodeId === void 0) {
        el.nodeId = transformContext.counter++;
        el.props.__nodeId = el.nodeId;
    }
    if (el.component === null || el.component === void 0) {
        if (typeof el.type === 'function') {
            // ES6 arrow function have no prototype.
            el.component = (el.type.prototype && el.type.prototype.transform) ?
                new el.type(el.props) :
                new RedAgateSimpleComponent(el.type, el.props);
        }
    }
    return el;
}
function duplicateElement(el) {
    return {
        type: el.type,
        props: el.props,
        nodeId: el.nodeId,
        children: (Array.isArray(el.children) ? el.children.slice(0) : el.children),
        component: el.component,
    };
}
export function transform(element, transformContext) {
    if (!transformContext) {
        transformContext = { counter: 0 };
    }
    if (element === null || element === void 0) {
        return element;
    }
    else if (Array.isArray(element)) {
        return (element
            .map(x => transform(x, transformContext))
            .filter(x => x !== null && x !== void 0));
    }
    else if (typeof element !== 'object') {
        return element;
    }
    const el = duplicateElement(constructComponent(element, transformContext));
    if (el.component && el.component.transform) {
        const z = el.component.transform();
        if (z === null || z === void 0) {
            el.children = [];
        }
        else if (Array.isArray(z)) {
            el.children = z;
        }
        else if (typeof z !== 'object') {
            el.children = [z];
        }
        else {
            el.children = [z];
        }
    }
    else if (!el.children) {
        el.children = [];
    }
    el.children = el.children
        .map(x => transform(x, transformContext))
        .filter(x => x !== null && x !== void 0);
    return el;
}
function camelToKebabCase(s) {
    return s.replace(/([a-z0-9])([A-Z])/g, (match, p1, p2) => `${p1}-${p2.toLowerCase()}`);
}
export function elementStyleRenderer(style) {
    return typeof style === 'string' ? style :
        Object.getOwnPropertyNames(style)
            .filter(x => style[x] !== null && style[x] !== void 0)
            .map(x => `${Escape.html(camelToKebabCase(x))}:${Escape.html(style[x])};`)
            .join('');
}
export function htmlAttributesRenderer(props, omitKeys, whiteListKeys) {
    let attrs = '';
    let children = '';
    const formatArrayProp = (prop) => {
        if (Array.isArray(prop)) {
            return (prop.map((x) => Escape.html(x.toString()))
                .filter(x => x !== null && x !== void 0)
                .join(' '));
        }
        else {
            if (prop === null || prop === void 0) {
                return '';
            }
            else
                switch (typeof prop) {
                    case 'function':
                    case 'object':
                    case 'symbol':
                        return '';
                    default:
                        return String(prop);
                }
        }
    };
    for (const key of Object.getOwnPropertyNames(props)) {
        switch (key) {
            case 'styleClass':
            case 'className':
            case 'class':
                attrs += ` class="${formatArrayProp(props[key])}"`;
                break;
            case 'style':
                attrs += ` style="${elementStyleRenderer(props.style)}"`;
                break;
            case 'children':
            case '__nodeId':
                break;
            case 'dangerouslySetInnerHTML':
                children += props[key].__html;
                break;
            case 'setInnerText':
                children += Escape.html(props[key].__text).replace(/\r?\n/g, '<br/>').replace(/\r/g, '<br/>');
                break;
            default:
                if (omitKeys && omitKeys.has(key)) {
                    // no output
                }
                else if (whiteListKeys && !whiteListKeys.has(key) && key !== 'id') {
                    // no output
                }
                else if (props[key] === null || props[key] === void 0 || props[key] === false) {
                    // no output
                }
                else if (props[key] === true) {
                    attrs += ` ${Escape.html(key)}`;
                }
                else {
                    attrs += ` ${Escape.html(key)}="${formatArrayProp(props[key])}"`;
                }
        }
    }
    return { attrs, children };
}
export function htmlRenderer(element, contexts) {
    if (element === null || element === void 0) {
        return '';
    }
    if (Array.isArray(element)) {
        return element.map(x => htmlRenderer(x, contexts)).join('');
    }
    if (typeof element !== 'object') {
        return Escape.html(element.toString());
    }
    if (!element.props) {
        return Escape.html(element.toString());
    }
    const el = element;
    // tslint:disable-next-line:prefer-const
    let { attrs, children } = htmlAttributesRenderer(el.props);
    if (el.component && el.component.beforeRender) {
        el.component.beforeRender(contexts);
    }
    if (el.children !== null && el.children !== void 0) {
        children += el.children.map(x => htmlRenderer(x, contexts)).join('');
    }
    let r = '';
    if (el.component && el.component.render) {
        r = el.component.render(contexts, children);
    }
    else if (typeof el.type === 'string') {
        const type = Escape.html(el.type);
        if ((children === '' || children === null || children === void 0) && HTML_NO_CLOSE_TAGS.has(type)) {
            r = `<${type}${attrs}/>`;
        }
        else {
            r = `<${type}${attrs}>${children}</${type}>`;
        }
    }
    else {
        r = children;
    }
    if (el.component && el.component.afterRender) {
        el.component.afterRender(contexts);
    }
    return r;
}
export function renderAsHtml(element) {
    const contexts = new Map();
    const transformContext = { counter: 0 };
    const z = transform(element, transformContext);
    return (Promise.all(getPromises([], z))
        .then(d => htmlRenderer(z, contexts)));
}
export function renderAsHtml_noDefer(element) {
    const contexts = new Map();
    const transformContext = { counter: 0 };
    const z = transform(element, transformContext);
    return htmlRenderer(z, contexts);
}
export function render(element, container, callback) {
    renderAsHtml(element)
        .then(html => {
        container.innerHTML = html;
        if (callback)
            callback(html, null);
    })
        .catch(error => {
        if (callback)
            callback(null, error);
    });
}
export function renderOnAwsLambda(element, callback) {
    renderAsHtml(element)
        .then(html => {
        callback(null, html);
    })
        .catch(error => {
        callback(error, null);
    });
}
export function renderOnExpress(element, req, res, headers) {
    renderAsHtml(element)
        .then(content => {
        if (headers) {
            headers.forEach((x) => res.set(x[0], typeof x[1] === 'string' ? x[1] : void 0));
        }
        res.send(content);
    })
        .catch(error => {
        console.error(JSON.stringify(error));
        res.status(500).send('');
    });
}
export class Fragment extends RedAgatePhantomComponent {
    constructor(props) {
        super(props);
    }
}
//# sourceMappingURL=red-agate.js.map